import { CLEAR_MESSAGE, LOGOUT } from "../constants/types";
import { clearPendingLoginChallenge } from "./authLoginChallengeStorage";
import { clearStoredPostLoginRedirect } from "./authPostLoginRedirectStorage";
import { clearPendingRegistrationChallenge } from "./authRegistrationChallengeStorage";
import { clearAccessToken } from "./authTokenStore";
import { suspendAuthRefresh } from "./authRefreshCoordinator";
import { ACCOUNT_DELETION_SSO_CALLBACK_STORAGE_KEY } from "./accountDeletionSsoCallback";

export const USER_SCOPED_CLIENT_STATE_CLEARED_EVENT = "muzone:user-scoped-state-cleared";

const LOCAL_STORAGE_KEYS = Object.freeze([
    "muzone.chat.selectedChat",
    "muzone.chat.pinned",
    "muzone.chat.muted",
    "muzone.bands.selectedChat",
    "muzone.bands.directIntent",
    "muzone.bands.selectedBandObject",
    "muzone.bands.activeApp",
    "muzone.bands.commentPanel",
    "muzone.bands.orbitPlanner.range",
    "muzone.bands.orbitPlanner.view",
    "muzone_active_tab",
]);

const LOCAL_STORAGE_PREFIXES = Object.freeze([
    "muzone.chat.hidden.",
]);

const SESSION_STORAGE_KEYS = Object.freeze([
    "muzone.bands.viewMode.v1",
    ACCOUNT_DELETION_SSO_CALLBACK_STORAGE_KEY,
]);

const SESSION_STORAGE_PREFIXES = Object.freeze([
    "muzone.profilePicture.",
]);

const cleaners = new Set();
let clearInFlight = null;

const getStorage = (storageName) => {
    if (typeof window === "undefined") {
        return null;
    }
    try {
        return window[storageName] || null;
    } catch (_) {
        return null;
    }
};

const removeKeys = (storage, exactKeys, prefixes) => {
    if (!storage) {
        return;
    }

    exactKeys.forEach((key) => {
        try {
            storage.removeItem(key);
        } catch (_) {
            // Continue clearing the remaining user-scoped entries.
        }
    });

    let keys = [];
    try {
        keys = Array.from({ length: storage.length }, (_, index) => storage.key(index))
            .filter(Boolean);
    } catch (_) {
        return;
    }

    keys.forEach((key) => {
        if (!prefixes.some((prefix) => key.startsWith(prefix))) {
            return;
        }
        try {
            storage.removeItem(key);
        } catch (_) {
            // Continue clearing the remaining user-scoped entries.
        }
    });
};

export const registerUserScopedClientStateCleaner = (cleaner) => {
    if (typeof cleaner !== "function") {
        throw new TypeError("cleaner must be a function");
    }
    cleaners.add(cleaner);
    return () => cleaners.delete(cleaner);
};

export const clearUserScopedClientState = ({ dispatch, broadcast = true } = {}) => {
    suspendAuthRefresh({ broadcast });
    clearAccessToken();
    clearPendingLoginChallenge();
    clearPendingRegistrationChallenge();
    clearStoredPostLoginRedirect();

    removeKeys(getStorage("localStorage"), LOCAL_STORAGE_KEYS, LOCAL_STORAGE_PREFIXES);
    removeKeys(getStorage("sessionStorage"), SESSION_STORAGE_KEYS, SESSION_STORAGE_PREFIXES);

    if (typeof dispatch === "function") {
        dispatch({ type: LOGOUT });
        dispatch({ type: CLEAR_MESSAGE });
    }

    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(USER_SCOPED_CLIENT_STATE_CLEARED_EVENT));
    }

    if (clearInFlight) {
        return clearInFlight;
    }

    const pendingCleaners = Array.from(cleaners, (cleaner) => {
        try {
            return Promise.resolve(cleaner());
        } catch (_) {
            return Promise.resolve();
        }
    });

    clearInFlight = Promise.allSettled(pendingCleaners)
        .then(() => undefined)
        .finally(() => {
            clearInFlight = null;
        });

    return clearInFlight;
};
